import { FC } from "react"
import styled, { css, keyframes } from "styled-components"
import sapling from "../../images/sapling.jpg"
import Color from "../../constants/Color"
import * as Animation from "../../functionality/Animation"

interface Props {
    isAnimatingBlur: boolean
    left: string
    bottom: string
    widthInPx: number
}

// The dimensions of the source image
const imageWidth = 1500
const imageHeight = 555
// const imageAspectRatio = (imageWidth / imageHeight)
const imageLeftOffset = 200 // The position of the left edge of the sapling in the image

const imageRightOffset: Animation.VariableValue = {
    end: 0,
    start: 400,
    endControl: 800,    // Width in px
    startControl: 400,    // Width in px
}

/**
 * A photo of a sapling with a gradient fading to opaque at the top of the image,
 * adjusted such that the sapling is at the same distance from the left edge
 * of the element. Also adjusts the size of the image at narrow widths such that the sapling
 * takes up a greater portion of the available room.
 */
const Sapling: FC<Props> = ({left, bottom, widthInPx, isAnimatingBlur}) => {

    const imageRightOffsetInPx = Animation.calculateValue(imageRightOffset, widthInPx)
    const imageScale = ((widthInPx + imageRightOffsetInPx) / (imageWidth - imageLeftOffset))
    const heightInPx = (imageHeight * imageScale)
    const imageLeftOffsetInPx = (imageLeftOffset * imageScale)
    
    return <Wrapper left={left} bottom={bottom} widthInPx={widthInPx} heightInPx={heightInPx}>
        <Image src={sapling} isAnimatingBlur={isAnimatingBlur} leftInPx={-imageLeftOffsetInPx} widthInPx={widthInPx + imageLeftOffsetInPx + imageRightOffsetInPx}/>
        <Overlay widthInPx={widthInPx} heightInPx={heightInPx * 0.8}/>
    </Wrapper>
}

export default Sapling

const Wrapper = styled.div.attrs<{left: string, bottom: string, widthInPx: number, heightInPx: number}>(p => ({
    style: {
        left: p.left,
        bottom: p.bottom,
        width: p.widthInPx + "px",
        height: p.heightInPx + "px",
    }
}))`
    position: absolute;
    overflow: hidden;
`

const blurKeyframes = keyframes`
    0% { filter: blur(30px) saturate(0); }
    100% { filter: blur(0px) saturate(0); }
`

const Image = styled.img.attrs<{leftInPx: number, widthInPx: number, isAnimatingBlur: boolean }>(p => ({
    style: {
        left: p.leftInPx + "px",
        width: p.widthInPx + "px",
    }
}))`
    position: absolute;
    bottom: 0;
    filter: saturate(0);
    animation: ${p => p.isAnimatingBlur ? css`${blurKeyframes} 10s forwards` : "none"};
`

const Overlay = styled.div.attrs<{widthInPx: number, heightInPx: number}>(p => ({
    style: {
        width: p.widthInPx + "px",
        height: p.heightInPx + "px",
    }
}))`
    position: absolute;
    top: 0;
    left: 0;
    background: linear-gradient(to bottom, ${Color.Black} 0%, ${Color.Black + "00"} 100%);
`